import React, { createContext, useState, useContext, useCallback } from 'react';

// 謄本購入リストのContextを作成
const TohonBuyContext = createContext();

/**
 * 謄本購入リストのContextを利用するためのカスタムフック
 * @returns {object} TohonBuyContextのvalue
 */
export const useTohonBuy = () => useContext(TohonBuyContext);

// 謄本購入リストの上限
const MAX_TOHON_BUY_LIST_LENGTH = 5;

/**
 * 謄本購入リストのデータを提供するProviderコンポーネント
 */
export const TohonBuyProvider = ({ children }) => {
  const [tohonBuyList, setTohonBuyList] = useState([]);

  /**
   * 渡された謄本情報がリストに存在するかどうかを確認する。
   * @param {object} tohonItem - チェック対象の謄本情報
   * @returns {boolean} 存在する場合true
   */
  const isTohonInList = useCallback((tohonItem) => {
    return tohonBuyList.some(item =>
      item.code === tohonItem.code &&
      item.city === tohonItem.city &&
      item.oaza === tohonItem.oaza &&
      item.chome === tohonItem.chome &&
      item.koaza === tohonItem.koaza &&
      item.yobi === tohonItem.yobi &&
      item.chiban === tohonItem.chiban
    );
  }, [tohonBuyList]);

  /**
   * 謄本をリストに追加する。
   * 上限に達している場合や、すでにリストに存在する場合は追加しない。
   * @param {object} tohonItem - 追加する謄本情報
   */
  const addTohon = useCallback((tohonItem) => {
    if (isTohonInList(tohonItem)) {
      console.log("すでに追加されています。");
      return;
    }

    if (tohonBuyList.length >= MAX_TOHON_BUY_LIST_LENGTH) {
      alert(`1回の購入は${MAX_TOHON_BUY_LIST_LENGTH}件までです。`);
      return;
    }

    setTohonBuyList(prevList => [...prevList, tohonItem]);
  }, [tohonBuyList, isTohonInList]);

  /**
   * 謄本をリストから削除する。
   * @param {object} tohonItem - 削除する謄本情報
   */
  const removeTohon = useCallback((tohonItem) => {
    setTohonBuyList(prevList =>
      prevList.filter(item =>
        !(item.code === tohonItem.code &&
          item.city === tohonItem.city &&
          item.oaza === tohonItem.oaza &&
          item.chome === tohonItem.chome &&
          item.koaza === tohonItem.koaza &&
          item.yobi === tohonItem.yobi &&
          item.chiban === tohonItem.chiban)
      )
    );
  }, []);

  /**
   * 謄本リストが上限に達していないかどうかを確認する。
   * @returns {boolean} 上限に達していない場合true
   */
  const isNotMaxTohonList = useCallback(() => {
    return tohonBuyList.length < MAX_TOHON_BUY_LIST_LENGTH;
  }, [tohonBuyList]);


  /**
   * 謄本リストを空にする。
   */
  const clearTohon = useCallback(() => {
    setTohonBuyList([]);
  }, []);

  /**
   * 謄本リストを指定の配列で復元する。
   * Stripe Checkout へ遷移して決済キャンセルで戻った際に、
   * sessionStorage から購入リストを復元して再決済できるようにするために使用する。
   * @param {Array} list - 復元する謄本購入リスト
   */
  const restoreTohon = useCallback((list) => {
    if (Array.isArray(list)) {
      setTohonBuyList(list.slice(0, MAX_TOHON_BUY_LIST_LENGTH));
    }
  }, []);

  const value = {
    tohonBuyList,
    addTohon,
    removeTohon,
    clearTohon,
    restoreTohon,
    isTohonInList,
    isNotMaxTohonList,
    maxTohonCount: MAX_TOHON_BUY_LIST_LENGTH,
  };

  return (
    <TohonBuyContext.Provider value={value}>
      {children}
    </TohonBuyContext.Provider>
  );
};
